import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'

import type { HeartbeatPushEntity } from 'api/aws/heartbeat/useHeartbeatPushTimeline'

/**
 * Cross-deal Heartbeat push timeline for the admin/diagnostics page.
 * Backed by `GET /aws/heartbeat/pushes` — a full MOTHERSHIP scan for
 * `heartbeat_push` entities, optionally filtered by status / flow /
 * trigger. Read-only.
 *
 * Not safe for high-frequency rendering — the BE scan is more expensive
 * than the per-deal PK query. Use only on the dedicated admin page.
 */

export interface AllPushesFilters {
  status?: 'success' | 'failure' | 'skipped' | 'resolved'
  flow?: 'A' | 'B' | 'IO' | 'none'
  trigger?: string
  /** Server-side cap; FE pagination layered on client side. */
  limit?: number
}

interface AllPushesResponse {
  success: boolean
  pushes: HeartbeatPushEntity[]
  returned: number
}

export const useAllHeartbeatPushes = (
  filters: AllPushesFilters = {},
): UseQueryResult<AllPushesResponse, Error> => {
  return useQuery<AllPushesResponse, Error>({
    queryKey: ['all-heartbeat-pushes', filters],
    staleTime: 30_000,
    queryFn: async (): Promise<AllPushesResponse> => {
      const params = new URLSearchParams()
      if (filters.status) params.set('status', filters.status)
      if (filters.flow) params.set('flow', filters.flow)
      if (filters.trigger) params.set('trigger', filters.trigger)
      if (filters.limit) params.set('limit', String(filters.limit))
      const qs = params.toString()
      const url = `${baseUrl}/aws/heartbeat/pushes${qs ? `?${qs}` : ''}`
      const res = await axios.get<AllPushesResponse>(url)
      return res.data
    },
  })
}
